import { IActionContext } from '@msdyn365-commerce/core';
import { searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

export interface ICategoryHasProductsMap {
    [categoryId: number]: boolean;
}

export interface ICategoryEntry {
    categoryId: number;
    catalogId: number;
}

interface ICacheRecord {
    at: number;
    key: string;
    data: ICategoryHasProductsMap;
}

const STORAGE_KEY = 'phb:categories-with-products:v1';
const TTL_MS = 30 * 60 * 1000; // 30 minutes
const MAX_CONCURRENCY = 4;

let memoryCache: ICacheRecord | undefined;
let inflight: { key: string; promise: Promise<ICategoryHasProductsMap> } | undefined;

function buildKey(channelId: number, entries: ICategoryEntry[]): string {
    const ids = entries.map(e => `${e.catalogId}:${e.categoryId}`).sort();
    return `${channelId}|${ids.join(',')}`;
}

function dedupe(entries: ICategoryEntry[]): ICategoryEntry[] {
    const seen = new Set<string>();
    return entries.filter(e => {
        const k = `${e.catalogId}:${e.categoryId}`;
        if (seen.has(k)) {
            return false;
        }
        seen.add(k);
        return true;
    });
}

function readCache(key: string): ICategoryHasProductsMap | undefined {
    const now = Date.now();

    if (memoryCache && memoryCache.key === key && now - memoryCache.at < TTL_MS) {
        return memoryCache.data;
    }

    try {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as ICacheRecord;
            if (parsed.key === key && now - parsed.at < TTL_MS) {
                memoryCache = parsed;
                return parsed.data;
            }
        }
    } catch {
        /* storage unavailable or corrupt: ignore */
    }
    return undefined;
}

function writeCache(record: ICacheRecord): void {
    memoryCache = record;
    try {
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
        }
    } catch {
        /* ignore */
    }
}

/** Runs `worker` over `items` with at most `limit` running at once. */
async function runWithLimit<T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
    let next = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (next < items.length) {
            const item = items[next++];
            await worker(item);
        }
    });
    await Promise.all(runners);
}

/** Uncached: one searchByCriteria call per category. Returns the map and whether every call succeeded. */
async function fetchCategoriesWithProducts(
    actionContext: IActionContext,
    channelId: number,
    entries: ICategoryEntry[]
): Promise<{ data: ICategoryHasProductsMap; complete: boolean }> {
    const data: ICategoryHasProductsMap = {};
    let complete = true;

    await runWithLimit(entries, MAX_CONCURRENCY, async ({ categoryId, catalogId }) => {
        const searchCriteria = {
            Context: { ChannelId: channelId, CatalogId: catalogId },
            CategoryIds: [categoryId]
        } as ProductSearchCriteria;

        try {
            const contextForCall = {
                ...actionContext,
                queryResultSettings: { Paging: { Top: 1, Skip: 0 } }
            };
            const products = await searchByCriteriaAsync(contextForCall as any, searchCriteria);
            data[categoryId] = !!products && products.length > 0;
        } catch (error) {
            console.error('searchByCriteriaAsync failed for category', categoryId, 'catalog', catalogId, error);
            // Fail open: don't hide a category just because the lookup failed
            data[categoryId] = true;
            complete = false;
        }
    });

    return { data, complete };
}

/**
 * Cached version. Safe to call from every page: hits memory, then sessionStorage,
 * and only calls the API when both are empty/expired. Concurrent callers share one request.
 */
export async function getCategoriesWithProducts(
    actionContext: IActionContext,
    channelId: number,
    entries: ICategoryEntry[]
): Promise<ICategoryHasProductsMap> {
    const unique = dedupe(entries);
    const key = buildKey(channelId, unique);

    const cached = readCache(key);
    if (cached) {
        return cached;
    }

    if (inflight && inflight.key === key) {
        return inflight.promise;
    }

    const promise = fetchCategoriesWithProducts(actionContext, channelId, unique)
        .then(({ data, complete }) => {
            if (complete) {
                writeCache({ at: Date.now(), key, data });
            }
            return data;
        })
        .finally(() => {
            inflight = undefined;
        });

    inflight = { key, promise };
    return promise;
}